/**
 * Compliance Services Content - ROC Filing, Director KYC, Changes, Closure
 */

// ==================== ROC COMPLIANCE ====================

export const rocFiling = {
  id: "roc-filing",
  slug: "roc-filing",
  seoTitle: "ROC Filing Services | MCA Compliance India",
  seoDescription: "Expert ROC filing services for companies and LLPs. Event-based filings, form preparation, and MCA compliance. Avoid penalties with timely filings.",
  heroTitle: "ROC Filing Services",
  heroSubtitle: "Corporate Compliance",
  summary: "ROC (Registrar of Companies) filings are mandatory for all registered companies and LLPs in India. These include event-based filings for changes in directors, share capital, registered address, and other corporate actions. Our experts ensure timely and accurate filings to keep your company compliant with MCA requirements.",
  
  whoNeeds: {
    title: "Who Needs ROC Filing Services?",
    description: "All companies and LLPs must file various forms with ROC for corporate events.",
    points: [
      "Companies appointing or removing directors",
      "Businesses changing registered office address",
      "Companies increasing authorized capital",
      "Firms transferring shares between shareholders",
      "Companies changing company name or objects",
      "LLPs changing partners or designated partners",
      "Companies creating or modifying charges",
      "Businesses filing resolutions and agreements"
    ]
  },
  
  whyRequired: {
    title: "Why is ROC Filing Important?",
    description: "Timely ROC filings are mandatory under Companies Act with penalties for delays.",
    points: [
      "Legal Requirement: Mandatory under Companies Act, 2013 with specific timelines.",
      "Avoid Penalties: Late filing attracts additional fees and penalties.",
      "Public Record: Updates company information on MCA public records.",
      "Due Diligence: Banks and investors verify ROC records before transactions.",
      "Director Protection: Protects directors from prosecution for non-compliance.",
      "Business Operations: Some operations require updated ROC filings."
    ]
  },
  
  benefits: [
    { title: "Expert Filing", description: "CA-prepared filings ensuring accuracy and compliance." },
    { title: "Timely Submission", description: "Never miss deadlines with our tracking system." },
    { title: "Documentation", description: "Complete documentation and resolution drafting." },
    { title: "DSC Management", description: "Digital signature handling for all filings." },
    { title: "Query Handling", description: "Respond to MCA queries and resubmissions." },
    { title: "Status Updates", description: "Real-time updates on filing status." },
    { title: "Compliance Calendar", description: "Track all upcoming filing deadlines." },
    { title: "Penalty Avoidance", description: "Avoid costly late filing penalties." }
  ],
  
  documentsRequired: {
    title: "Documents for ROC Filing",
    description: "Requirements vary based on type of filing.",
    directors: [
      "Director DIN and DSC",
      "Identity and address proof",
      "Consent letters (for appointments)",
      "Resignation letters (if applicable)"
    ],
    company: [
      "Board resolution",
      "Shareholder resolution (if required)",
      "Supporting documents for the change",
      "Previous filing records",
      "Company incorporation documents",
      "Latest financial statements"
    ]
  },
  
  process: [
    { step: 1, title: "Requirement Analysis", description: "Identify applicable forms and requirements for your corporate action.", duration: "Same day" },
    { step: 2, title: "Document Collection", description: "Gather necessary documents, resolutions, and approvals.", duration: "1-2 days" },
    { step: 3, title: "Form Preparation", description: "Prepare e-form with accurate data and attachments.", duration: "1 day" },
    { step: 4, title: "DSC Affixing", description: "Affix digital signatures of authorized persons.", duration: "Same day" },
    { step: 5, title: "Filing & Payment", description: "Upload form to MCA portal and pay applicable fees.", duration: "Same day" },
    { step: 6, title: "Approval Tracking", description: "Track approval status and handle any queries.", duration: "7-15 days" }
  ],
  
  timeline: {
    total: "7-21 days for approval",
    breakdown: [
      { phase: "Document preparation", duration: "1-3 days" },
      { phase: "Form filing", duration: "1 day" },
      { phase: "MCA processing", duration: "7-15 days" }
    ],
    note: "Most forms have 30-day deadline from event date. STP forms approved faster than non-STP."
  },
  
  pricing: {
    title: "ROC Filing Fees",
    description: "Per-filing pricing for various MCA forms.",
    packages: [
      {
        name: "Single Filing",
        price: "₹2,999",
        description: "One e-form filing",
        features: [
          "Form preparation",
          "Resolution drafting",
          "MCA portal filing",
          "Query handling",
          "Certificate delivery"
        ]
      },
      {
        name: "Multiple Filings",
        price: "₹6,999",
        description: "Up to 3 forms",
        features: [
          "All Single Filing features",
          "Combined documentation",
          "Priority handling",
          "Compliance review",
          "Extended support"
        ],
        popular: true
      },
      {
        name: "Annual Package",
        price: "₹19,999",
        description: "Unlimited filings",
        features: [
          "All event-based filings",
          "Annual compliance",
          "Compliance calendar",
          "Dedicated manager",
          "Priority support"
        ]
      }
    ],
    governmentFees: "Government fees vary: ₹200-2,000 per form. Late fees additional."
  },
  
  faqs: [
    { question: "What is the deadline for ROC filings?", answer: "Most event-based filings must be done within 30 days of the event (board meeting, resolution date). Some forms have 15-day or 60-day deadlines." },
    { question: "What happens if I miss the deadline?", answer: "Late filings attract additional fees. Fees increase progressively - from 2x to 12x of normal fees depending on delay period." },
    { question: "What is STP and non-STP filing?", answer: "STP (Straight Through Processing) forms are auto-approved by MCA system. Non-STP forms require manual review and take longer." },
    { question: "Can ROC reject a filing?", answer: "Yes, filings can be marked 'defective' or rejected for errors, incomplete information, or non-compliance. We handle resubmissions." },
    { question: "Do I need separate filings for each change?", answer: "Generally yes, each corporate action requires specific form. Some related changes can be combined in one filing." },
    { question: "How do I check filing status?", answer: "Status is available on MCA portal using SRN (Service Request Number). We provide regular updates on your filings." }
  ],
  
  relatedServices: [
    { id: "annual-filing", label: "Annual Compliance" },
    { id: "director-kyc", label: "Director KYC" },
    { id: "company-name-change", label: "Name Change" },
    { id: "share-transfer", label: "Share Transfer" },
    { id: "registered-address-change", label: "Address Change" },
    { id: "virtual-cfo", label: "Virtual CFO" }
  ],
  
  trustSignals: {
    incorporations: "15,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

export const directorKyc = {
  id: "director-kyc",
  slug: "director-kyc",
  seoTitle: "Director KYC Filing | DIR-3 KYC Online India",
  seoDescription: "Complete annual Director KYC (DIR-3 KYC) filing before September 30. Mandatory for all company directors. Expert filing services with quick processing.",
  heroTitle: "Director KYC (DIR-3 KYC)",
  heroSubtitle: "Corporate Compliance",
  summary: "Director KYC is an annual mandatory filing for all directors of Indian companies. Every director must update their KYC details using Form DIR-3 KYC on MCA portal by September 30 each year. Failure to file leads to DIN deactivation and inability to act as director. Our experts ensure timely filing with proper documentation.",
  
  whoNeeds: {
    title: "Who Must File Director KYC?",
    description: "All directors holding DIN must complete annual KYC verification.",
    points: [
      "All directors of Private Limited Companies",
      "Directors of Public Limited Companies",
      "Directors of One Person Companies",
      "Designated Partners of LLPs (with DIN)",
      "Independent directors and nominee directors",
      "Directors of Section 8 companies",
      "Foreign directors of Indian companies",
      "Directors whose DIN was deactivated"
    ]
  },
  
  whyRequired: {
    title: "Why is Director KYC Mandatory?",
    description: "Annual verification ensures authentic director information on MCA records.",
    points: [
      "Legal Mandate: Mandatory under Companies (Appointment and Qualification of Directors) Rules.",
      "DIN Activation: DIN gets deactivated if KYC not filed by deadline.",
      "Prevent Fraud: Helps government verify genuine directors and prevent shell companies.",
      "Business Continuity: Deactivated DIN means you cannot act as director.",
      "Reactivation Penalty: Reactivating deactivated DIN costs ₹5,000 extra.",
      "Compliance Record: Clean KYC record for due diligence and bank relations."
    ]
  },
  
  benefits: [
    { title: "Timely Filing", description: "Never miss September 30 deadline with our reminders." },
    { title: "Easy Process", description: "We handle the entire filing process online." },
    { title: "OTP Handling", description: "Guide you through mobile and email OTP verification." },
    { title: "DSC Support", description: "Help with DSC issues and digital signature affixing." },
    { title: "Multiple Directors", description: "File for all directors of your company together." },
    { title: "Reactivation", description: "Help reactivate DIN if already deactivated." },
    { title: "Foreign Directors", description: "Special handling for non-resident directors." },
    { title: "Quick Processing", description: "Same-day filing with immediate confirmation." }
  ],
  
  documentsRequired: {
    title: "Documents for Director KYC",
    description: "Current documents required for KYC verification.",
    directors: [
      "PAN Card",
      "Aadhaar Card (Indian directors)",
      "Passport (mandatory for foreign directors)",
      "Current residential address proof",
      "Personal mobile number linked to Aadhaar",
      "Personal email ID",
      "Digital Signature Certificate"
    ],
    company: [
      "DIN (Director Identification Number)",
      "List of all directorships held",
      "Company CIN numbers"
    ]
  },
  
  process: [
    { step: 1, title: "Document Collection", description: "Gather PAN, Aadhaar, address proof and contact details.", duration: "Same day" },
    { step: 2, title: "Form Preparation", description: "Fill DIR-3 KYC form with accurate details.", duration: "1 hour" },
    { step: 3, title: "OTP Verification", description: "Verify mobile and email through OTP.", duration: "10 minutes" },
    { step: 4, title: "DSC Affixing", description: "Affix director's digital signature.", duration: "5 minutes" },
    { step: 5, title: "Filing", description: "Submit form on MCA portal.", duration: "Immediate" },
    { step: 6, title: "Confirmation", description: "Receive acknowledgment and SRN.", duration: "Immediate" }
  ],
  
  timeline: {
    total: "Same day processing",
    breakdown: [
      { phase: "Document collection", duration: "1-2 hours" },
      { phase: "Form filling & verification", duration: "30 minutes" },
      { phase: "Filing & confirmation", duration: "Immediate" }
    ],
    note: "Deadline: September 30 every year. File early to avoid last-minute rush."
  },
  
  pricing: {
    title: "Director KYC Pricing",
    description: "Simple pricing for annual KYC filing.",
    packages: [
      {
        name: "Single Director",
        price: "₹999",
        description: "One director KYC",
        features: [
          "Form preparation",
          "OTP assistance",
          "DSC guidance",
          "MCA filing",
          "Acknowledgment"
        ]
      },
      {
        name: "Two Directors",
        price: "₹1,799",
        description: "KYC for 2 directors",
        features: [
          "Both director filings",
          "Coordinated processing",
          "All Single features",
          "Priority support",
          "Reminder service"
        ],
        popular: true
      },
      {
        name: "Company Package",
        price: "₹2,999",
        description: "Up to 5 directors",
        features: [
          "All director KYCs",
          "Bulk processing",
          "Compliance calendar",
          "Annual reminders",
          "Dedicated support"
        ]
      }
    ],
    governmentFees: "No government fee if filed by September 30. Late filing: ₹5,000 penalty."
  },
  
  faqs: [
    { question: "What is the deadline for Director KYC?", answer: "September 30 every year. Directors who got DIN allotted during the year must file within 30 days of allotment." },
    { question: "What if I miss the deadline?", answer: "Your DIN will be marked 'Deactivated due to non-filing of DIR-3 KYC'. You cannot act as director until reactivated with ₹5,000 penalty." },
    { question: "Is DSC mandatory for Director KYC?", answer: "Yes, valid Class 2 or Class 3 DSC of the director is required for filing DIR-3 KYC." },
    { question: "Can I file KYC with changed address?", answer: "Yes, you should file with your current address. Director KYC also serves to update your address in MCA records." },
    { question: "What OTPs are needed?", answer: "OTP will be sent to your registered mobile number and email ID linked to your Aadhaar (for Indian directors) for verification." },
    { question: "How to reactivate deactivated DIN?", answer: "File DIR-3 KYC with ₹5,000 late fee. DIN gets reactivated after successful filing and payment." }
  ],
  
  relatedServices: [
    { id: "annual-filing", label: "Annual Compliance" },
    { id: "roc-filing", label: "ROC Filing" },
    { id: "director-appointment", label: "Director Appointment" },
    { id: "din-allotment", label: "DIN Allotment" },
    { id: "dsc-registration", label: "DSC Registration" },
    { id: "private-limited-company", label: "Company Registration" }
  ],
  
  trustSignals: {
    incorporations: "20,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

// ==================== COMPANY CHANGES ====================

export const companyNameChange = {
  id: "company-name-change",
  slug: "company-name-change",
  seoTitle: "Company Name Change | Change Company Name in India",
  seoDescription: "Change your company's registered name with MCA. Complete name change process including name approval, ROC filing, and certificate update. Expert assistance.",
  heroTitle: "Company Name Change",
  heroSubtitle: "Corporate Changes",
  summary: "Changing your company name requires approval from the Ministry of Corporate Affairs and involves multiple filings. Whether you're rebranding, expanding services, or complying with trademark requirements, we handle the complete name change process from name availability check to updated Certificate of Incorporation.",
  
  whoNeeds: {
    title: "Who Needs Company Name Change?",
    description: "Companies requiring to change their registered name for various reasons.",
    points: [
      "Companies undergoing rebranding or repositioning",
      "Businesses expanding into new sectors or markets",
      "Companies ordered by trademark authorities to change name",
      "Firms merging or acquiring other businesses",
      "Companies with names similar to existing trademarks",
      "Businesses removing location-specific names for expansion",
      "Companies updating name to reflect new ownership",
      "Firms aligning company name with brand identity"
    ]
  },
  
  whyRequired: {
    title: "Why Formal Name Change Process?",
    description: "Legal process ensures valid name change and updates all records.",
    points: [
      "Legal Recognition: New name gets legal recognition only after MCA approval.",
      "Updated Certificate: Receive fresh Certificate of Incorporation with new name.",
      "MCA Records: All company records updated with new name.",
      "Contracts & Banking: Legal basis to update all business relationships.",
      "Trademark Protection: Enables trademark registration for new name.",
      "Credibility: Official name change maintains business continuity."
    ]
  },
  
  benefits: [
    { title: "Name Search", description: "Check availability and reserve your preferred name." },
    { title: "Complete Filing", description: "Handle all required MCA forms and filings." },
    { title: "Resolution Drafting", description: "Draft board and shareholder resolutions." },
    { title: "Certificate Update", description: "Obtain updated Certificate of Incorporation." },
    { title: "PAN/TAN Update", description: "Assistance with PAN and TAN name update." },
    { title: "Bank Update", description: "Support letter for bank account name change." },
    { title: "GST Update", description: "Help update GST registration with new name." },
    { title: "Document Templates", description: "Templates for informing stakeholders." }
  ],
  
  documentsRequired: {
    title: "Documents for Name Change",
    description: "Documentation required for company name change.",
    directors: [
      "Director DSCs (all directors)",
      "Director identity proofs",
      "Board resolution for name change"
    ],
    company: [
      "Current Certificate of Incorporation",
      "MOA and AOA",
      "Special resolution from shareholders",
      "Explanatory statement for name change",
      "NOC from existing name owner (if applicable)",
      "Trademark search report for new name"
    ]
  },
  
  process: [
    { step: 1, title: "Name Availability", description: "Check if proposed name is available with MCA through RUN service.", duration: "1-2 days" },
    { step: 2, title: "Name Reservation", description: "Reserve approved name for 60 days.", duration: "On approval" },
    { step: 3, title: "Board Meeting", description: "Conduct board meeting to approve name change.", duration: "1 day" },
    { step: 4, title: "EGM/Postal Ballot", description: "Pass special resolution for name change through EGM.", duration: "7-21 days" },
    { step: 5, title: "ROC Filing", description: "File MGT-14 and INC-24 with ROC.", duration: "3-5 days" },
    { step: 6, title: "New Certificate", description: "Receive updated Certificate of Incorporation.", duration: "7-15 days" }
  ],
  
  timeline: {
    total: "30-45 days",
    breakdown: [
      { phase: "Name approval (RUN)", duration: "3-7 days" },
      { phase: "Shareholder approval", duration: "7-21 days" },
      { phase: "ROC filing & certificate", duration: "15-21 days" }
    ],
    note: "Timeline depends on shareholder meeting logistics. Can be expedited with postal ballot."
  },
  
  pricing: {
    title: "Name Change Fees",
    description: "Complete name change service including all filings.",
    packages: [
      {
        name: "Standard",
        price: "₹14,999",
        description: "Complete name change",
        features: [
          "Name availability check",
          "RUN filing",
          "Resolution drafting",
          "MGT-14 & INC-24 filing",
          "New certificate"
        ]
      },
      {
        name: "Express",
        price: "₹19,999",
        description: "Priority processing",
        features: [
          "All Standard features",
          "Postal ballot handling",
          "Priority filing",
          "PAN/TAN update support",
          "GST update assistance"
        ],
        popular: true
      },
      {
        name: "Complete",
        price: "₹29,999",
        description: "End-to-end support",
        features: [
          "All Express features",
          "Bank account update",
          "All registrations update",
          "Stakeholder communication",
          "6-month support"
        ]
      }
    ],
    governmentFees: "Government fees: RUN ₹1,000 + Filing fees ₹3,000-6,000 based on authorized capital."
  },
  
  faqs: [
    { question: "What names can I choose?", answer: "Name must be unique, not identical/similar to existing companies or trademarks, not offensive, and should reflect business activities if including sector-specific words." },
    { question: "Can name change be rejected?", answer: "Yes, MCA can reject names that are similar to existing names, infringe trademarks, or violate naming guidelines. We do thorough checks before filing." },
    { question: "Do I need trademark for new name?", answer: "Recommended but not mandatory. We advise trademark registration for brand protection after name change." },
    { question: "Will company CIN change?", answer: "No, CIN remains same. Only the name portion in records gets updated. All historical records remain intact." },
    { question: "What documents need updating after name change?", answer: "PAN, TAN, GST, bank accounts, contracts, letterheads, signage, website, and all business registrations need updating." },
    { question: "Can I change name multiple times?", answer: "Yes, but each change requires full process and fees. Also, frequent changes may raise questions during due diligence." }
  ],
  
  relatedServices: [
    { id: "trademark-registration", label: "Trademark Registration" },
    { id: "registered-address-change", label: "Address Change" },
    { id: "moa-amendment", label: "MOA Amendment" },
    { id: "roc-filing", label: "ROC Filing" },
    { id: "gst-amendment", label: "GST Amendment" },
    { id: "private-limited-company", label: "Company Registration" }
  ],
  
  trustSignals: {
    incorporations: "5,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

export const registeredAddressChange = {
  id: "registered-address-change",
  slug: "registered-address-change",
  seoTitle: "Registered Office Address Change | Company Address Change",
  seoDescription: "Change your company's registered office address. Within city, inter-city, or inter-state address change with all ROC filings. Expert compliance assistance.",
  heroTitle: "Registered Address Change",
  heroSubtitle: "Corporate Changes",
  summary: "Changing your company's registered office address requires ROC filings to update MCA records. The process varies based on whether you're moving within the same city, to another city in same state, or to a different state. We handle all documentation and filings for seamless address change.",
  
  whoNeeds: {
    title: "Who Needs Address Change Filing?",
    description: "Companies moving their registered office to a new location.",
    points: [
      "Companies relocating to larger premises",
      "Businesses moving to different cities",
      "Companies shifting to different states",
      "Startups moving from home to commercial address",
      "Businesses consolidating multiple offices",
      "Companies moving to co-working spaces",
      "Firms changing from virtual to physical office",
      "Companies updating from old to new address format"
    ]
  },
  
  whyRequired: {
    title: "Why File Address Change?",
    description: "Legal compliance and official record update is mandatory.",
    points: [
      "Legal Requirement: Companies Act mandates ROC filing within 30 days of change.",
      "Official Records: MCA records must reflect current address for legal notices.",
      "GST Compliance: GST registration address must match registered office.",
      "Bank Requirements: Banks verify registered address for various services.",
      "Legal Notices: Important legal notices are served at registered address.",
      "Penalty Avoidance: Late filing attracts additional fees and penalties."
    ]
  },
  
  benefits: [
    { title: "Process Guidance", description: "Expert guidance on applicable process based on move type." },
    { title: "Documentation", description: "Complete documentation and resolution preparation." },
    { title: "ROC Filing", description: "Handle all applicable MCA filings." },
    { title: "RD Approval", description: "Manage Regional Director approval for inter-state moves." },
    { title: "Certificate Update", description: "Obtain updated certificate if applicable." },
    { title: "GST Update", description: "Assist with GST address amendment." },
    { title: "Quick Processing", description: "Expedited processing to minimize disruption." },
    { title: "Compliance Check", description: "Ensure all related compliances are updated." }
  ],
  
  documentsRequired: {
    title: "Documents for Address Change",
    description: "Documentation requirements for registered address change.",
    directors: [
      "Board resolution approving address change",
      "Director DSCs for filing"
    ],
    company: [
      "New address proof (rent agreement/ownership)",
      "Utility bill of new premises",
      "NOC from premises owner",
      "Special resolution (for inter-state)",
      "Creditors' consent (for inter-state)",
      "Current MOA and AOA"
    ]
  },
  
  process: [
    { step: 1, title: "Assess Move Type", description: "Determine if within local limits, inter-city, or inter-state.", duration: "Same day" },
    { step: 2, title: "Board Resolution", description: "Pass board resolution approving new address.", duration: "1 day" },
    { step: 3, title: "Special Resolution", description: "For inter-state: Pass special resolution in EGM.", duration: "If applicable" },
    { step: 4, title: "ROC Filing", description: "File INC-22 (local/inter-city) or INC-23 (inter-state).", duration: "1-2 days" },
    { step: 5, title: "RD Approval", description: "For inter-state: Obtain Regional Director approval.", duration: "30-60 days" },
    { step: 6, title: "Update Records", description: "Update GST, bank, and other registrations.", duration: "As applicable" }
  ],
  
  timeline: {
    total: "7-60 days based on move type",
    breakdown: [
      { phase: "Within local limits", duration: "7-15 days" },
      { phase: "Inter-city (same state)", duration: "15-30 days" },
      { phase: "Inter-state", duration: "45-60 days" }
    ],
    note: "Inter-state moves require Regional Director approval and take longer."
  },
  
  pricing: {
    title: "Address Change Fees",
    description: "Pricing based on type of address change.",
    packages: [
      {
        name: "Local Change",
        price: "₹4,999",
        description: "Within same city",
        features: [
          "Board resolution",
          "INC-22 filing",
          "Document preparation",
          "MCA tracking",
          "GST update guidance"
        ]
      },
      {
        name: "Inter-City",
        price: "₹7,999",
        description: "Different city, same state",
        features: [
          "All Local features",
          "Special resolution",
          "Extended documentation",
          "All ROC filings",
          "Complete support"
        ],
        popular: true
      },
      {
        name: "Inter-State",
        price: "₹19,999",
        description: "Different state",
        features: [
          "All Inter-City features",
          "RD application",
          "Creditor notices",
          "Complete representation",
          "MOA amendment"
        ]
      }
    ],
    governmentFees: "Government fees: INC-22 ₹500, INC-23 ₹2,000. RD fees additional for inter-state."
  },
  
  faqs: [
    { question: "What is the deadline for filing address change?", answer: "INC-22 must be filed within 30 days of passing board resolution. Verification of registered office must be done within 30 days of incorporation/change." },
    { question: "What is local limits vs inter-city?", answer: "Local limits means within the same city/town jurisdiction. Inter-city is different city within same state. Inter-state is moving to different state." },
    { question: "Is shareholder approval needed?", answer: "For moves within local limits and inter-city: Board resolution sufficient. For inter-state: Special resolution (75% majority) required." },
    { question: "Can I use co-working space address?", answer: "Yes, co-working space can be used as registered address with proper documentation (agreement, NOC, utility bill)." },
    { question: "What about virtual office address?", answer: "Virtual office address can be used if you have legitimate agreement and the provider allows company registration. Physical mail handling must be possible." },
    { question: "Do I need to update GST separately?", answer: "Yes, GST registration needs separate amendment for address change. We assist with GST update process." }
  ],
  
  relatedServices: [
    { id: "company-name-change", label: "Name Change" },
    { id: "roc-filing", label: "ROC Filing" },
    { id: "gst-amendment", label: "GST Amendment" },
    { id: "authorized-capital-increase", label: "Capital Increase" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "virtual-cfo", label: "Virtual CFO" }
  ],
  
  trustSignals: {
    incorporations: "8,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

export const shareTransfer = {
  id: "share-transfer",
  slug: "share-transfer",
  seoTitle: "Share Transfer in Private Company | Transfer Shares India",
  seoDescription: "Transfer shares in private limited company with proper documentation. Share transfer deed, board approval, stamp duty, and ROC filing. Expert CA services.",
  heroTitle: "Share Transfer",
  heroSubtitle: "Corporate Changes",
  summary: "Share Transfer is the process of transferring ownership of shares from one person to another. In private limited companies, share transfers require board approval, proper documentation, stamp duty payment, and ROC filing. We ensure legally compliant transfer with all necessary formalities and documentation.",
  
  whoNeeds: {
    title: "Who Needs Share Transfer Services?",
    description: "Shareholders and companies facilitating ownership changes.",
    points: [
      "Founders selling equity to investors",
      "Shareholders exiting the company",
      "Employees exercising ESOPs",
      "Family businesses transferring to next generation",
      "Companies facilitating management buyouts",
      "Investors acquiring stakes in companies",
      "Shareholders gifting shares to family",
      "Companies implementing share buyback"
    ]
  },
  
  whyRequired: {
    title: "Why Proper Share Transfer Process?",
    description: "Legal transfer requires proper documentation and compliance.",
    points: [
      "Legal Ownership: Proper transfer establishes legal ownership of shares.",
      "ROC Records: Transfer must be recorded in ROC for legal recognition.",
      "Tax Compliance: Stamp duty and capital gains tax implications.",
      "Shareholder Rights: New owner gets voting and dividend rights.",
      "Due Diligence: Documented transfer essential for future transactions.",
      "Company Records: Member register must be updated."
    ]
  },
  
  benefits: [
    { title: "Documentation", description: "Prepare share transfer deed and all documents." },
    { title: "Board Resolution", description: "Draft board resolution approving transfer." },
    { title: "Stamp Duty", description: "Calculate and arrange stamp duty payment." },
    { title: "ROC Filing", description: "File SH-4 with Registrar of Companies." },
    { title: "Certificate Update", description: "Issue new share certificates to transferee." },
    { title: "Tax Advisory", description: "Guidance on capital gains tax implications." },
    { title: "Valuation Support", description: "Help with share valuation if needed." },
    { title: "Compliance", description: "Ensure FEMA compliance for foreign transfers." }
  ],
  
  documentsRequired: {
    title: "Documents for Share Transfer",
    description: "Documentation required for share transfer.",
    directors: [
      "Board resolution approving transfer",
      "Director DSCs for ROC filing"
    ],
    company: [
      "Share Transfer Deed (SH-4)",
      "Original share certificates",
      "NOC from existing shareholders (if required)",
      "PAN of transferor and transferee",
      "Identity proofs of both parties",
      "Share valuation report (if applicable)",
      "FEMA compliance documents (for foreign transfers)"
    ]
  },
  
  process: [
    { step: 1, title: "Transfer Agreement", description: "Finalize terms between transferor and transferee.", duration: "As agreed" },
    { step: 2, title: "Valuation", description: "Determine fair market value if required.", duration: "2-5 days" },
    { step: 3, title: "Documentation", description: "Prepare share transfer deed and supporting documents.", duration: "1-2 days" },
    { step: 4, title: "Stamp Duty", description: "Pay applicable stamp duty on transfer deed.", duration: "1 day" },
    { step: 5, title: "Board Approval", description: "Obtain board approval for the transfer.", duration: "1 day" },
    { step: 6, title: "ROC Filing", description: "File SH-4 and update member register.", duration: "7-15 days" }
  ],
  
  timeline: {
    total: "15-30 days",
    breakdown: [
      { phase: "Documentation preparation", duration: "3-5 days" },
      { phase: "Stamp duty & board approval", duration: "3-7 days" },
      { phase: "ROC filing & certificate", duration: "7-15 days" }
    ],
    note: "Timeline varies based on valuation requirements and board meeting schedule."
  },
  
  pricing: {
    title: "Share Transfer Fees",
    description: "Professional fees for share transfer services.",
    packages: [
      {
        name: "Basic Transfer",
        price: "₹7,999",
        description: "Simple transfers",
        features: [
          "Transfer deed drafting",
          "Board resolution",
          "ROC filing (SH-4)",
          "Certificate issuance",
          "Register update"
        ]
      },
      {
        name: "Standard",
        price: "₹14,999",
        description: "With valuation",
        features: [
          "All Basic features",
          "Share valuation",
          "Tax advisory",
          "Stamp duty calculation",
          "Complete documentation"
        ],
        popular: true
      },
      {
        name: "Foreign Transfer",
        price: "₹24,999",
        description: "Cross-border transfers",
        features: [
          "All Standard features",
          "FEMA compliance",
          "RBI reporting",
          "Foreign investor docs",
          "Tax treaty analysis"
        ]
      }
    ],
    governmentFees: "Stamp duty: 0.015% to 0.25% of consideration (varies by state). ROC filing: ₹200."
  },
  
  faqs: [
    { question: "Can private company shares be freely transferred?", answer: "No, private company shares have transfer restrictions. Board approval is typically required as per Articles of Association." },
    { question: "What is stamp duty on share transfer?", answer: "Stamp duty varies by state, typically 0.015% to 0.25% of consideration or market value, whichever is higher." },
    { question: "Is share transfer taxable?", answer: "Yes, transferor may have capital gains tax liability. Transferee may have TDS obligations. We advise on tax implications." },
    { question: "Can shares be gifted without consideration?", answer: "Yes, shares can be gifted. However, gift tax provisions may apply. Proper documentation and valuation still required." },
    { question: "What about transfer to foreign nationals?", answer: "Transfer to non-residents requires FEMA compliance, RBI approval in some cases, and additional documentation." },
    { question: "How is share value determined?", answer: "For tax purposes, value determined by CA-certified valuation using DCF, NAV, or other prescribed methods." }
  ],
  
  relatedServices: [
    { id: "authorized-capital-increase", label: "Capital Increase" },
    { id: "private-limited-company", label: "Company Registration" },
    { id: "roc-filing", label: "ROC Filing" },
    { id: "esop-implementation", label: "ESOP" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "shareholder-agreement", label: "Shareholder Agreement" }
  ],
  
  trustSignals: {
    incorporations: "6,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

// ==================== CLOSURE ====================

export const companyStrikeOff = {
  id: "company-strike-off",
  slug: "company-strike-off",
  seoTitle: "Company Strike Off | Remove Company from ROC Register",
  seoDescription: "Strike off your dormant company from MCA register. Fast-track strike off under Section 248. Expert CA services for company closure.",
  heroTitle: "Company Strike Off",
  heroSubtitle: "Company Closure",
  summary: "Company Strike Off is the process of removing a company from the MCA register when it's no longer carrying on business. Under Section 248 of Companies Act, companies can apply for voluntary strike off if they haven't conducted business for 2 years or have no assets/liabilities. We handle the complete strike off process.",
  
  whoNeeds: {
    title: "Who Should Strike Off Their Company?",
    description: "Inactive companies meeting strike off criteria.",
    points: [
      "Companies not doing business for 2+ years",
      "Dormant companies with no future plans",
      "Companies with no assets or liabilities",
      "Failed startups with minimal activity",
      "Shell companies to be closed",
      "Companies incorporated but never operated",
      "Companies wanting faster closure than winding up",
      "Subsidiaries being consolidated"
    ]
  },
  
  whyRequired: {
    title: "Why Strike Off Your Company?",
    description: "Strike off provides clean closure for inactive companies.",
    points: [
      "End Compliance: Stop annual filing and compliance requirements.",
      "Director Relief: Free directors from continuing obligations.",
      "Avoid Penalties: Stop accumulating non-compliance penalties.",
      "Clean Exit: Proper closure better than being struck off by ROC.",
      "Faster Process: Quicker than formal winding up.",
      "Cost Effective: Less expensive than liquidation process."
    ]
  },
  
  benefits: [
    { title: "Eligibility Check", description: "Verify if company qualifies for voluntary strike off." },
    { title: "Pending Compliance", description: "Clear pending filings before strike off application." },
    { title: "Documentation", description: "Prepare indemnity bond, affidavit, and NOCs." },
    { title: "ROC Filing", description: "File STK-2 application with ROC." },
    { title: "Objection Handling", description: "Handle any objections from stakeholders." },
    { title: "Asset Settlement", description: "Guidance on settling any remaining assets." },
    { title: "Liability Clearance", description: "Ensure all liabilities are cleared." },
    { title: "Final Order", description: "Obtain strike off order from ROC." }
  ],
  
  documentsRequired: {
    title: "Documents for Strike Off",
    description: "Documentation required for voluntary strike off.",
    directors: [
      "Director DSCs",
      "Indemnity bond from all directors",
      "Affidavit from directors",
      "Identity and address proofs"
    ],
    company: [
      "Special resolution for strike off",
      "Statement of accounts (not older than 30 days)",
      "NOC from regulatory authorities",
      "Copy of MOA and AOA",
      "List of all pending litigation (if any)",
      "Details of any assets/liabilities"
    ]
  },
  
  process: [
    { step: 1, title: "Eligibility Assessment", description: "Verify company meets strike off criteria.", duration: "1 day" },
    { step: 2, title: "Clear Pending Filings", description: "File all pending annual returns and compliances.", duration: "As needed" },
    { step: 3, title: "Board & EGM", description: "Pass board resolution and special resolution.", duration: "7-21 days" },
    { step: 4, title: "Documentation", description: "Prepare indemnity bond, affidavit, statement of accounts.", duration: "3-5 days" },
    { step: 5, title: "STK-2 Filing", description: "File strike off application with ROC.", duration: "1 day" },
    { step: 6, title: "Public Notice", description: "ROC publishes notice, waiting period for objections.", duration: "3 months" },
    { step: 7, title: "Strike Off Order", description: "ROC issues strike off order if no objections.", duration: "30 days" }
  ],
  
  timeline: {
    total: "4-6 months",
    breakdown: [
      { phase: "Preparation & filing", duration: "30-45 days" },
      { phase: "Public notice period", duration: "3 months" },
      { phase: "Final order", duration: "30 days" }
    ],
    note: "3-month notice period is mandatory. Total time depends on pending compliance status."
  },
  
  pricing: {
    title: "Strike Off Fees",
    description: "Complete strike off service pricing.",
    packages: [
      {
        name: "Basic Strike Off",
        price: "₹14,999",
        description: "Compliant companies",
        features: [
          "Application preparation",
          "All documentation",
          "STK-2 filing",
          "ROC coordination",
          "Final order"
        ]
      },
      {
        name: "With Compliance",
        price: "₹24,999",
        description: "With pending filings",
        features: [
          "Clear 2 years backlog",
          "All strike off work",
          "Penalty minimization",
          "Complete documentation",
          "Full support"
        ],
        popular: true
      },
      {
        name: "Complex Cases",
        price: "₹39,999",
        description: "With complications",
        features: [
          "Extended backlog filing",
          "Litigation handling",
          "Asset settlement",
          "Stakeholder management",
          "Complete representation"
        ]
      }
    ],
    governmentFees: "Government fee: ₹5,000-10,000 based on authorized capital. Backlog penalty additional."
  },
  
  faqs: [
    { question: "What are the eligibility criteria for strike off?", answer: "Company should not have conducted business for 2 years, have no assets or liabilities, and should not have pending regulatory investigations." },
    { question: "What happens to pending liabilities?", answer: "All liabilities must be cleared or provided for. Directors remain personally liable for any undisclosed liabilities even after strike off." },
    { question: "Can struck off company be restored?", answer: "Yes, company can be restored within 20 years by court order if there's valid reason and pending matters to be addressed." },
    { question: "What if there are objections?", answer: "ROC considers objections during notice period. Genuine objections may delay or prevent strike off. We help address objections." },
    { question: "Do I need NOC from income tax?", answer: "Tax clearance is recommended. Pending tax demands can result in objections. We advise clearing tax matters before filing." },
    { question: "What about bank accounts?", answer: "Bank accounts should be closed and balances settled before strike off. Banks may freeze accounts of struck off companies." }
  ],
  
  relatedServices: [
    { id: "company-winding-up", label: "Winding Up" },
    { id: "llp-closure", label: "LLP Closure" },
    { id: "annual-filing", label: "Annual Compliance" },
    { id: "director-resignation", label: "Director Resignation" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "gst-cancellation", label: "GST Cancellation" }
  ],
  
  trustSignals: {
    incorporations: "3,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

export const llpClosure = {
  id: "llp-closure",
  slug: "llp-closure",
  seoTitle: "LLP Closure India | Close LLP Online",
  seoDescription: "Close your LLP through strike off or winding up. Complete LLP closure process with expert CA assistance. Clear compliances and close legally.",
  heroTitle: "LLP Closure",
  heroSubtitle: "Business Closure",
  summary: "LLP Closure can be done through voluntary strike off under Section 75 of LLP Act when the LLP is not carrying on business. The process requires clearing all pending compliances, settling liabilities, and filing strike off application with ROC. We handle complete LLP closure with proper documentation.",
  
  whoNeeds: {
    title: "Who Should Close Their LLP?",
    description: "LLPs that are no longer active or required.",
    points: [
      "LLPs not operating for 1+ year",
      "LLPs with no business activity planned",
      "Partners wanting to exit the business",
      "LLPs converting to company structure",
      "Failed ventures incorporated as LLP",
      "LLPs with no assets or liabilities",
      "Dormant LLPs accumulating compliance burden",
      "LLPs being consolidated or merged"
    ]
  },
  
  whyRequired: {
    title: "Why Formally Close LLP?",
    description: "Proper closure ends compliance obligations and provides clean exit.",
    points: [
      "End Compliance: Stop annual filing requirements (Form 8 & 11).",
      "Partner Relief: Free partners from continuing liabilities.",
      "Avoid Penalties: Stop accumulating late filing penalties.",
      "Clean Record: Better than being struck off by ROC for default.",
      "Simple Process: Easier than company winding up.",
      "Cost Saving: Stop paying for compliance and accounting."
    ]
  },
  
  benefits: [
    { title: "Eligibility Assessment", description: "Verify LLP qualifies for voluntary closure." },
    { title: "Compliance Clearance", description: "File pending Form 8 and Form 11 returns." },
    { title: "Documentation", description: "Prepare closure application and all annexures." },
    { title: "Partner Consent", description: "Obtain consent from all partners." },
    { title: "ROC Filing", description: "File Form 24 for LLP strike off." },
    { title: "Objection Management", description: "Handle any objections during notice period." },
    { title: "Tax Clearance", description: "Guidance on tax closure and filings." },
    { title: "Final Order", description: "Obtain strike off order from ROC." }
  ],
  
  documentsRequired: {
    title: "Documents for LLP Closure",
    description: "Documentation required for LLP strike off.",
    directors: [
      "Designated Partner DSCs",
      "Consent of all partners",
      "Affidavit from designated partners",
      "Indemnity bond"
    ],
    company: [
      "Statement of accounts (not older than 30 days)",
      "NOC from creditors/regulatory authorities",
      "Asset/liability statement",
      "Bank account closure proof",
      "Tax return filings",
      "All pending Form 8 and Form 11"
    ]
  },
  
  process: [
    { step: 1, title: "Eligibility Check", description: "Verify LLP meets closure criteria - no business for 1 year.", duration: "1 day" },
    { step: 2, title: "Clear Compliances", description: "File all pending annual returns (Form 8 & 11).", duration: "As needed" },
    { step: 3, title: "Partner Consent", description: "Obtain consent from all partners for closure.", duration: "1-7 days" },
    { step: 4, title: "Settle Liabilities", description: "Clear all debts and dispose assets.", duration: "As needed" },
    { step: 5, title: "Documentation", description: "Prepare Form 24 and all supporting documents.", duration: "3-5 days" },
    { step: 6, title: "ROC Filing", description: "File Form 24 with ROC.", duration: "1 day" },
    { step: 7, title: "Notice Period", description: "Wait for public notice and objection period.", duration: "3 months" },
    { step: 8, title: "Strike Off", description: "ROC issues strike off order.", duration: "30 days" }
  ],
  
  timeline: {
    total: "4-6 months",
    breakdown: [
      { phase: "Compliance clearance", duration: "15-30 days" },
      { phase: "Documentation & filing", duration: "7-15 days" },
      { phase: "Notice period", duration: "3 months" },
      { phase: "Final order", duration: "30 days" }
    ],
    note: "Timeline depends on compliance backlog and liability settlement."
  },
  
  pricing: {
    title: "LLP Closure Fees",
    description: "Complete LLP closure service.",
    packages: [
      {
        name: "Basic Closure",
        price: "₹9,999",
        description: "Compliant LLPs",
        features: [
          "Form 24 preparation",
          "Documentation",
          "ROC filing",
          "Notice management",
          "Final order"
        ]
      },
      {
        name: "With Compliance",
        price: "₹17,999",
        description: "With pending returns",
        features: [
          "2 years Form 8 & 11",
          "All Basic features",
          "Penalty management",
          "Tax return support",
          "Complete closure"
        ],
        popular: true
      },
      {
        name: "Complex Closure",
        price: "₹29,999",
        description: "Extended backlog",
        features: [
          "Extended compliance",
          "Liability settlement",
          "Asset disposal",
          "Partner disputes",
          "Full representation"
        ]
      }
    ],
    governmentFees: "Government fee: ₹1,000-3,000. Late filing penalties additional based on backlog."
  },
  
  faqs: [
    { question: "What is the criteria for LLP closure?", answer: "LLP should not have conducted business for 1 year or more, should have no assets or liabilities, and partners should consent to closure." },
    { question: "Can LLP be restored after strike off?", answer: "Yes, LLP can be restored within 15 years by application to NCLT if there are valid grounds." },
    { question: "What about pending GST registration?", answer: "GST registration should be cancelled before LLP closure. Final GST return must be filed." },
    { question: "Do all partners need to consent?", answer: "Yes, all partners must sign consent for voluntary strike off. Without unanimous consent, other closure routes may be needed." },
    { question: "What if there are pending liabilities?", answer: "All liabilities must be settled before closure. Partners remain liable for undisclosed debts even after strike off." },
    { question: "Is auditor required for closure?", answer: "LLPs with turnover above ₹40 lakh or contribution above ₹25 lakh need audited accounts. Statement of accounts must be certified." }
  ],
  
  relatedServices: [
    { id: "company-strike-off", label: "Company Strike Off" },
    { id: "annual-filing", label: "LLP Compliance" },
    { id: "gst-cancellation", label: "GST Cancellation" },
    { id: "income-tax-filing", label: "Tax Filing" },
    { id: "llp-registration", label: "LLP Registration" },
    { id: "private-limited-company", label: "Convert to Company" }
  ],
  
  trustSignals: {
    incorporations: "2,500+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert CAs"
  }
};

// Export all compliance services
export const complianceChangeServices = {
  rocFiling,
  directorKyc,
  companyNameChange,
  registeredAddressChange,
  shareTransfer,
  companyStrikeOff,
  llpClosure
};
